import { useEffect, useMemo, useState } from 'react';
import { distanceKmFromUser as distanceKmFromUserUtil } from '../components/guest-map/distance';
import { fetchApprovedExperiences } from '../utils/experiencesApi';
import { NEARBY_RADIUS_KM } from '../components/guest-map/guestMapConstants';

/**
 * Owns everything Hidden-Gems / experiences data for the map:
 *   - fetch approved experiences within 20 km of the guest
 *   - the radius-gated `displayedExperiences` render list
 *   - `nearbyExperienceCount` for the toggle badge
 *
 * The backend applies a 20 km server-side radius so we only refetch
 * when a live user position is available. Empty state when location
 * is missing matches the strict location gate the rest of the map
 * enforces.
 */
export function useGuestMapExperienceData({
  userPosition,
  locationStatus,
  hiddenGemsOn,
  selectedExperienceTags,
  radiusKm = NEARBY_RADIUS_KM,
}) {
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    if (locationStatus !== 'live' || !userPosition) {
      setExperiences((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    fetchApprovedExperiences({ lat: userPosition[0], lng: userPosition[1] })
      .then((rows) => setExperiences(Array.isArray(rows) ? rows.filter((r) => (
        typeof r.latitude === 'number' && typeof r.longitude === 'number'
      )) : []))
      .catch((err) => console.error('Error fetching experiences:', err));
  }, [locationStatus, userPosition]);

  const nearbyExperienceCount = useMemo(() => {
    if (locationStatus !== 'live' || !userPosition) return 0;
    return experiences.filter((e) => {
      const d = distanceKmFromUserUtil(
        { latitude: e.latitude, longitude: e.longitude },
        userPosition,
      );
      return d <= radiusKm;
    }).length;
  }, [experiences, locationStatus, userPosition, radiusKm]);

  // Experience pins rendered on the map — gated on live location + 20 km
  // radius + Hidden Gems toggle + selected experience tags. Independent
  // from the venue filter pipeline so a venue tag never hides a gem and
  // vice versa (guests can combine "burger" with "views-scenic").
  const displayedExperiences = useMemo(() => {
    if (!hiddenGemsOn) return [];
    if (locationStatus !== 'live' || !userPosition) return [];
    let list = experiences.filter((e) => {
      const d = distanceKmFromUserUtil(
        { latitude: e.latitude, longitude: e.longitude },
        userPosition,
      );
      return d <= radiusKm;
    });
    if (selectedExperienceTags.length > 0) {
      const wanted = selectedExperienceTags.map((t) => (t || '').trim().toLowerCase());
      list = list.filter((e) => (
        Array.isArray(e.tags) && e.tags.some((tag) => (
          wanted.includes((tag || '').trim().toLowerCase())
        ))
      ));
    }
    return list;
  }, [experiences, hiddenGemsOn, locationStatus, userPosition, selectedExperienceTags, radiusKm]);

  return {
    experiences,
    setExperiences,
    displayedExperiences,
    nearbyExperienceCount,
  };
}
